import { Injectable } from "@angular/core";
import { LoggerService } from "../../telemetry/logger.service";
import { TelemetryIdentifiers } from "../../telemetry/telemetry-identifiers";
import { BehaviorSubject, EMPTY, Observable, Subject, interval, timer } from "rxjs";
import { takeWhile, tap, catchError, exhaustMap, map, retry, take, finalize } from "rxjs/operators";
import { BackgroundOperationState } from "../models/enums/background-operation-state.enum";
import { BackgroundOperationDataService } from "./background-operation-data.service";
import { BackgroundOperationViewModel } from "../models/view-models/background-operation.view-model";
import { BackgroundOperationStatusDto } from "../models/dtos/background-operation-status.dto";
import { ToastService } from "src/app/toast/toast.service";
import { BackgroundOperationType } from "../models/enums/background-operation-type.enum";

/**
 * Service to manage background operations within this application.
 * Maintains an in-memory list of background operations and polls the API to get the status of active background operations.
 * Emits events when background operations complete.
 * Emits an updated list of background operations when their status changes.
 */
@Injectable({
	providedIn: 'root'
})
export class BackgroundOperationsService {
	/** The interval in milliseconds to poll for background operation status. */
	private readonly POLLING_INTERVAL_MS: number = 5000;

	/** The number of times to retry a failed polling API request before marking the operation as failed. */
	private readonly POLLING_RETRY_COUNT: number = 3;

	/** A dictionary of current background operations being monitored. */
	private operations: Map<string, BackgroundOperationViewModel> = new Map<string, BackgroundOperationViewModel>();

	/** BehaviorSubject that emits the current list of background operations. */
	private operations$: BehaviorSubject<BackgroundOperationViewModel[]> = new BehaviorSubject<BackgroundOperationViewModel[]>([]);

	/** Subject that emits when a background operation completes (not processing). */
	private operationCompleted$: Subject<BackgroundOperationViewModel> = new Subject<BackgroundOperationViewModel>();

	/**
	 * Emits the current list of background operations and updates on any changes.
	 * New subscribers immediately receive the latest list and subsequent updates.
	 * This includes both processing and completed operations since the last page refresh.
	 * @readonly
	 * @returns An observable emitting the list of background operations.
	 */
	public get Operations$(): Observable<BackgroundOperationViewModel[]> {
		return this.operations$.asObservable();
	}

	/**
	 * Emits when a background operation completes (no longer processing).
	 * Subscribers will receive the completed background operation object with result data (if success) and error message (if failed).
	 * @readonly
	 * @returns An observable emitting the list of background operations.
	 */
	public get OperationCompleted$(): Observable<BackgroundOperationViewModel> {
		return this.operationCompleted$.asObservable();
	}

	/**
	 * @constructor
	 * 
	 * @param logger The logger service used to track telemetry
	 * @param backgroundOperationDataService The service to interact with background operation apis
	 * @param toastService The toast service to show user notifications
	 */
	public constructor(
		private logger: LoggerService,
		private backgroundOperationDataService: BackgroundOperationDataService,
		private toastService: ToastService
	) { }

	/**
	 * Adds a new pending background operation to the list for operations that haven't started processing yet.
	 * @param operation The background operation to add.
	 */
	public AddPendingBackgroundOperation(operation: BackgroundOperationViewModel): void {
		this.operations.set(operation.ClientOperationId, operation);
		this.operations$.next(this.GetOperationsInReverseOrder());
	}

	/**
	 * Removes a background operation from the list, typically used for operations that are pending and haven't started processing yet.
	 * @param operation The background operation to remove.
	 */
	public RemovePendingBackgroundOperation(operation: BackgroundOperationViewModel): void {
		this.operations.delete(operation.ClientOperationId);
		this.operations$.next(this.GetOperationsInReverseOrder());
	}

	/**
	 * Starts polling to check if the operation has completed
	 * @param operation The background operation to start polling
	 */
	public StartPollingBackgroundOperation(operation: BackgroundOperationViewModel): void {
		this.logger.TrackEvent(TelemetryIdentifiers.EVENT_BACKGROUND_OPERATION_ADDED_TO_MONITOR, this.GetDimensions(operation));
		this.StartPolling(operation);
		this.operations$.next(this.GetOperationsInReverseOrder());
	}

	/**
	 * Cancels a background operation.
	 * Polls once after cancellation to update the operation status in the UI.
	 * @param operation The operation to cancel
	 * @returns An observable that completes when the cancellation request is done.
	 */
	public CancelBackgroundOperation$(operation: BackgroundOperationViewModel): Observable<void> {
		if (!operation || !operation.BackgroundOperationId) {
			throw new Error("Cannot cancel background operation: BackgroundOperation or BackgroundOperationId is null or undefined");
		}

		return this.backgroundOperationDataService.CancelBackgroundOperation$(operation).pipe(take(1),
			tap(() => {
				this.PollOnce(operation);
				this.logger.TrackEvent(TelemetryIdentifiers.EVENT_BACKGROUND_OPERATION_CANCELED, this.GetDimensions(operation));
			})
		);
	}

	/**
	 * Returns the first (most recent) background operation that matches the provided predicate or `undefined` if no match is found.
	 * Supports both standard boolean predicates and type guard predicates for type narrowing.
	 * - If a standard boolean predicate is provided, returns a `BackgroundOperationViewModel` or `undefined`.
	 * - If a type guard predicate is provided, returns a narrowed type `T` or `undefined`.
	 * @param predicate A function to test each operation. Can be a boolean predicate or a type guard.
	 * @returns The first matching operation (most recent) or `undefined` if no match is found.
	 * @example
	 * // Find a BackgroundOperationViewModel using a standard boolean predicate:
	 * const failedOperation: BackgroundOperationViewModel | undefined = service.FindOperation(o => o.Status === BackgroundOperationState.Failed);
	 * @example
	 * // Find a specific subclass using a type guard predicate:
	 * const esriOperation: EsriBackgroundOperationViewModel | undefined = service.FindOperation((o): o is EsriBackgroundOperationViewModel => 
	 *     o instanceof EsriBackgroundOperationViewModel && o.RoutePlanId === targetId);
	 */
	// Overload 1: For specific type narrowing using type guard predicate (Returns T)
	public FindOperation<T extends BackgroundOperationViewModel>(predicate: (operation: BackgroundOperationViewModel) => operation is T): T | undefined;

	// Overload 2: For standard boolean predicate (Returns BackgroundOperationViewModel)
	public FindOperation(predicate: (operation: BackgroundOperationViewModel) => boolean): BackgroundOperationViewModel | undefined;

	// Hidden Implementation:
	public FindOperation<T extends BackgroundOperationViewModel>(predicate: (operation: BackgroundOperationViewModel) => boolean): T | undefined {
		return this.GetOperationsInReverseOrder().find(predicate) as T | undefined;
	}

	/**
	 * Starts polling for the status of a background operation.
	 * @param operation The background operation to poll.
	 */
	private StartPolling(operation: BackgroundOperationViewModel): void {
		interval(this.POLLING_INTERVAL_MS)
			.pipe(
				takeWhile(() => operation.IsProcessing),
				exhaustMap(() => {
					return this.Poll$(operation);
				})
			)
			.subscribe();
	}

	/**
	 * Polls once for the status of a background operation.
	 * This should be used only when a single poll is needed, such as when the user cancels an operation as to update its status & the UI immediately.
	 * @param operation The background operation to poll.
	 */
	private PollOnce(operation: BackgroundOperationViewModel): void {
		if (!operation || !operation.BackgroundOperationId) {
			throw new Error("Cannot poll background operation: BackgroundOperationId is null or undefined");
		}
		const latestOperation: BackgroundOperationViewModel | undefined = this.operations.get(operation.ClientOperationId);
		if (!latestOperation?.IsPolling && latestOperation?.BackgroundOperationId)
		{
			this.Poll$(latestOperation).pipe(take(1)).subscribe();
		}
	}

	/**
	 * Emits the updated operations and notifies subscribers that an operation has completed.
	 * @param operation The background operation that completed.
	 */
	private EmitResults(operation: BackgroundOperationViewModel): void {
		// Emit the updated operations array.
		this.operations$.next(this.GetOperationsInReverseOrder());

		// Emit the completed operation event so subscribers can react and update their state.
		this.operationCompleted$.next(operation);

		// Show a toast notification based on the operation status.
		switch (operation.Status) {
			case BackgroundOperationState.Success:
				this.toastService.ShowInfo(`${operation.Description} completed successfully.`);
				break;
			case BackgroundOperationState.Failed:
				this.toastService.ShowError(operation.ErrorMessage || `${operation.Description} failed.`);
				const errorMessage = this.GetBackgroundOperationErrorMessage(operation);
				this.logger.TrackError(errorMessage, new Error(errorMessage), this.GetDimensions(operation));
				break;
			case BackgroundOperationState.Canceled:
				this.toastService.ShowInfo(`${operation.Description} was canceled.`);
				break;
		}
	}

	/**
	 * Gets the telemetry dimensions for a background operation and its result.
	 * @param operation The background operation view model.
	 * @returns An object containing telemetry dimensions.
	 */
	private GetDimensions(operation: BackgroundOperationViewModel): { [key: string]: any } {
		return {
			BackgroundOperationId: operation.BackgroundOperationId || "Unknown",
			Description: operation.Description || "Unknown",
			Status: operation.Status.toString(),
			Type: operation.Type.toString(),
			ErrorMessage: operation.ErrorMessage || "Unknown",
		};
	}

	/**
	 * Polls the API for the status of a background operation.
	 * Only calls once. The caller is responsible for handling repeated polling if needed.
	 * @param operation The background operation to poll.
	 * @returns An observable that emits the status of the background operation.
	 */
	private Poll$(operation: BackgroundOperationViewModel): Observable<BackgroundOperationViewModel> {
		if (operation?.IsPolling) {
			// Don't even create the HTTP observable if we're already polling
			return EMPTY;
		}
		
		operation.IsPolling = true;
		this.operations.set(operation.ClientOperationId, operation);
		
		return this.backgroundOperationDataService.GetBackgroundOperationStatus$(operation).pipe(
					// Retry failed api requests before giving up
					retry({
						count: this.POLLING_RETRY_COUNT,
						delay: (error: any, retryCount: number) => {
							const dimensions = { ...this.GetDimensions(operation), RetryCount: retryCount.toString() };
							this.logger.TrackError("Error polling background operation status, will retry.", error, dimensions);
							this.logger.TrackMetric(TelemetryIdentifiers.METRIC_BACKGROUND_OPERATION_POLLING_RETRY, 1, dimensions);
							// Use linear backoff based on polling interval: 5s, 10s, 15s
							const delayMs = this.POLLING_INTERVAL_MS * retryCount;
							return timer(delayMs);
						}
			}),
			map((result: BackgroundOperationStatusDto) => new BackgroundOperationViewModel(result)),
			tap((result: BackgroundOperationViewModel) => {
				if (!result.IsProcessing) {
					// Update the operation with the final status and result data.
					operation.Status = result.Status;
					operation.ResultData = result.ResultData;
					operation.ErrorMessage = result.ErrorMessage;

					this.EmitResults(operation);
					this.logger.TrackMetric(TelemetryIdentifiers.METRIC_BACKGROUND_OPERATION_COMPLETED, 1, this.GetDimensions(operation));
				}
			}),
			catchError((error: any) => {
				const dimensions: { [key: string]: any } = this.GetDimensions(operation);
				this.logger.TrackError("Error polling background operation status.", error, dimensions);
				this.logger.TrackMetric(TelemetryIdentifiers.METRIC_BACKGROUND_OPERATION_POLLING_ERROR, 1, dimensions);

				operation.Status = BackgroundOperationState.Failed;
				operation.ErrorMessage = `${operation.Description} failed. Please try again.`;
				this.EmitResults(operation);

				return EMPTY;
			}),
			finalize(() => {
				operation.IsPolling = false;
				this.operations.set(operation.ClientOperationId, operation);
			})
		);
	}

	/**
	 * Gets the array of background operations in reverse order (newest first).
	 * @returns An array of BackgroundOperationViewModel objects.
	 */
	private GetOperationsInReverseOrder(): BackgroundOperationViewModel[] {
		return Array.from(this.operations.values()).reverse();
	}

	/**
	 * Gets the error message for a background operation based on the operation type.
	 * @param operation The background operation result.
	 * @returns a string containing the error message
	 */
	private GetBackgroundOperationErrorMessage(operation: BackgroundOperationViewModel): string {
		if (operation.ErrorMessage) {
			return operation.ErrorMessage;
		}

		switch (operation.Type) {
			case BackgroundOperationType.EsriOptimizeRoute:
				return 'An error occurred while optimizing the route.';
			case BackgroundOperationType.EsriCalculateEtas:
				return 'An error occurred while calculating ETAs.';
			case BackgroundOperationType.EsriCalculateAllEtas:
				return 'An error occurred while calculating all ETAs.';
			default:
				return 'An error occurred while processing a background operation.';
		}
	}
}